import { ChangeDetectorRef, Component, EventEmitter, HostListener, Inject, NgZone, OnDestroy, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DealerLocatorSettings } from 'src/@omnial/_models/dealer-locator/dealer-locator.settings.model';
import { Dealer } from 'src/@omnial/_models/dealer-locator/dealer.model';
import { DealerService } from 'src/@omnial/_services/dealers/dealer.service';
import { OmnialMarker } from 'src/@omnial/_models/dealer-locator/marker.model';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { ScriptLoaderService } from 'src/@omnial/_services/external/script-load.service';
import PlaceResult = google.maps.places.PlaceResult;
import { Country } from 'src/@omnial/_models/order/country.model';
import { Manufacturer } from 'src/@omnial/_models/catalog/manufacturer.model';
import { TrackingService } from 'src/@omnial/_services/external/tracking.service';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-dealer-locator-map',
    templateUrl: './../../../../app/shared/dealer-locator/dealer-locator-map/dealer-locator-map.component.html',
    styleUrls: ['./../../../../app/shared/dealer-locator/dealer-locator-map/dealer-locator-map.component.scss'],
    standalone: false
})

export class DealerLocatorMapComponent implements OnDestroy {
  @Output() dealerSelected = new EventEmitter<Dealer>();
  @ViewChild('googleMap', { static: false }) map: GoogleMap;
  @ViewChildren(MapInfoWindow) infoWindowsView: QueryList<MapInfoWindow>;
  @ViewChildren(MapMarker) mapMarker: QueryList<MapMarker>;
  public settings: DealerLocatorSettings;
  public dealers: Dealer[];
  public manufacturers: Manufacturer[];
  public isMobile = false;
  public mapInit = false;
  public lat: number = 0;
  public lng: number = 0;
  public zoom: number = 4;
  public style: any[] = [];
  public radius: number = 20;
  public openedWindow: number;
  public markers: any[] = [];
  public infoContent: string;
  public searchControl: FormControl;
  public locations: OmnialMarker[];
  public locationList: OmnialMarker[];
  public selectedLocation: OmnialMarker;
  public subscriptions: Subscription[] = [];
  public center: google.maps.LatLng;
  public options: google.maps.MapOptions;
  public autoCompleteOptions: any;
  public countries: Country[] = [];
  public mapMoveTimer: NodeJS.Timeout;

  constructor(@Inject(DOCUMENT) private _document: Document,
    public dealerService: DealerService,
    public scriptLoaderService: ScriptLoaderService,
    public cd: ChangeDetectorRef,
    public zone: NgZone,
    public trackingService: TrackingService) {
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isMobile = window.innerWidth < 768;
    if (this.isMobile) {
      this.zoom = 2;
    } else {
      this.zoom = 4;
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
  }

  init() {
    window['angularGoogleMapsReference'] = { component: this, zone: this.zone, googleMapsLoaded: () => this.googleMapsLoaded() };
    this.subscriptions.push(this.scriptLoaderService.scripts.subscribe(
      res => {
        const googleScript = res?.find(s => s.ref === 'Google Maps');
        const googleClusterScript = res?.find(s => s.ref === 'Google Maps Cluster');
        if (googleScript?.loaded && googleClusterScript?.loaded) {
          this.initComponent();
        }
        else {
          if (!googleScript?.loading) {
            this.scriptLoaderService.load('Google Maps');
          }
          if (!googleClusterScript?.loading) {
            this.scriptLoaderService.load('Google Maps Cluster');
          }
        }
      }));
  }

  initComponent() {
    // HACK Styles are added TIME AND TIME again by Google Maps so for the pre render site we want to save a bit of file space
    if (environment.versionCheckUrl.indexOf("://pre.") !== -1) {
      const styleTags = this._document.head.getElementsByTagName('style');
      let deleteIndexes: number[] = [];
      if (styleTags) {
        for (var i = 0; i < styleTags.length; i++) {
          if (styleTags[i].innerText.startsWith('@keyframes beginBrowserAutofill{0%{}to{}}@keyframes endBrowserAutofill{0%{}to{}}.pac-container')) {
            deleteIndexes.push(i);
          }
        }
        for (var i = 0; i < deleteIndexes.length - 2; i++) {
          styleTags[deleteIndexes[i]].remove();
        }
      }
    }
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
    this.isMobile = window.innerWidth < 768;
    this.subscriptions.push(this.scriptLoaderService.scripts.subscribe(
      res => {
        if (res && res.find(s => s.ref === 'Google Maps')?.loaded && res.find(s => s.ref === 'Google Maps Cluster')?.loaded) {
          this.zoom = this.settings.mapZoomLevel;
          if (this.isMobile) {
            this.zoom = this.zoom - 2;
          }
          if (this.settings.mapStyleJson) {
            this.style = JSON.parse(this.settings.mapStyleJson) as any[];
          }
          this.radius = this.settings.radius;
          this.center = new google.maps.LatLng(this.settings.latitude, this.settings.longitude);
          this.options = {
            maxZoom: 18,
            minZoom: this.zoom,
            fullscreenControl: true,
            styles: this.style
          }
          this.mapInit = true;
          this.initMap();
        }
      }));
  }

  public initMap() {
    const defaultBounds = {
      north: this.settings.latitudeNorthWest,
      south: this.settings.latitudeSouthEast,
      east: this.settings.longitudeSouthEast,
      west: this.settings.longitudeNorthWest
    };
    this.autoCompleteOptions = {
      bounds: defaultBounds,
      fields: ["formatted_address", "geometry"],
      strictBounds: true,
      types: ['(regions)']
    };
    this.searchControl = new FormControl();
    this.locations = this.dealerService.getMarkers(this.dealers);
    // Ensure we have the Manufacturers
    this.locations?.forEach((location) => {
      location.pin = 'pin';
      location.manufacturers?.forEach(manufacturer => {
        const manufacturerFound = this.manufacturers.find(m => m.id === manufacturer.id);
        if (manufacturerFound) {
          location.pin = manufacturerFound.seName; // So we can customise the pin if we want
          manufacturer.name = manufacturerFound.name;
          manufacturer.pictureUrl = manufacturerFound.pictureUrl;
        }
      });
    });
    this.locationList = this.locations;
    this.markers = this.locations;
    this.sortLocations();
    if (this.lat && this.lng) {
      this.zoomToNewLocation(this.lat, this.lng, 11, true, true);
    }
  }

  public setCurrentPosition() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.lat = position.coords.latitude;
        this.lng = position.coords.longitude;
        this.center = new google.maps.LatLng(this.lat, this.lng);
        this.zoom = 11;
        this.sortLocations();
        this.mapInit = false;
        setTimeout(() => { this.mapInit = true });
      });
    }
  }

  public autoComplete(result: PlaceResult) {
    if (result?.geometry?.location) {
      this.zoomToNewLocation(result.geometry.location.lat(), result.geometry.location.lng(), 11, true, true)
    }
  }

  public formatRadius(value: number) {
    return value + 'km';
  }

  public onRadiusChange(radius: number) {
    if (radius) {
      this.radius = radius;
    }
  }

  public moveMap() {
    if (this.mapMoveTimer) {
      return;
    }
    const center = this.map.getCenter();
    if (center.lat() === this.center.lat() && center.lng() == this.center.lng()) { // Map has not moved yet
      return;
    }
    this.mapMoveTimer = setTimeout(() => {
      this.lat = center.lat();
      this.lng = center.lng();
      this.mapMoveTimer = null;
      this.sortLocations();
    }, 500);
  }

  public zoomToNewLocation(lat: number, lng: number, zoom: number, sort: boolean, init: boolean) {
    this.lat = lat;
    this.lng = lng;
    this.center = new google.maps.LatLng(this.lat, this.lng);
    this.zoom = zoom;
    if (sort) {
      this.sortLocations();
    }
    if (init) {
      this.mapInit = false;
      setTimeout(() => { this.mapInit = true; this.cd.detectChanges(); }, 200);
    }
  }

  public isInfoWindowOpen(index: number) {
    return this.openedWindow === index;
  }

  public openWindow(marker: MapMarker, index: number) {
    var location = this.locations[index];
    if (!location) {
      return;
    }
    this.zoomToNewLocation(location.lat, location.lng, 13, true, false);
    setTimeout(() => {
      this.infoWindowsView.forEach((infoWindow: MapInfoWindow) => {
        infoWindow.open(marker);
        this.selectedLocation = location;
        this.emitDealer(location);
      });
    });
  }

  public showDealer(location: OmnialMarker) {
    this.zoomToNewLocation(location.lat, location.lng, 13, false, false);
    setTimeout(() => {
      this.infoWindowsView.forEach((infoWindow: MapInfoWindow) => {
        const marker = this.mapMarker.find(m => m.getTitle() == location.title);
        if (marker) {
          infoWindow.open(marker);
        }
        this.selectedLocation = location;
        this.emitDealer(location);
        const dealer = this.dealers.find(d => d.id === location.dealerId);
        if (dealer) {
          this.trackingService.pushPage(`dealer/${dealer.code}`, `${dealer.name} ${dealer.address?.city} ${dealer.address?.state}`);
        }
      });
      this.sortLocations();
      this.cd.detectChanges();
    });
  }

  public dealerWebsiteClick(marker: OmnialMarker): void {
    const dealer = this.dealers.find(d => d.id === marker.dealerId);
    if (dealer) {
      this.trackingService.pushPage(`dealer-website/${dealer.code}`, `${dealer.name} ${dealer.address?.city} ${dealer.address?.state}`);
      this.trackingService.pushSelectContent('DealerWebsite', dealer.code);
    }
  }

  public setDealer(dealer: Dealer): void {
    if (dealer) {
      const location = this.locations.find(l => l.dealerId === dealer.id);
      if (location) {
        this.showDealer(location);
      }
    }
  }

  public emitDealer(location: OmnialMarker): void {
    if (location) {
      const selectedDealer = this.dealers.find(d => d.id === location.dealerId);
      if (selectedDealer) {
        this.dealerSelected.emit(selectedDealer);
      }
    }
  }

  distanceToCenter(lat: number, lng: number): number {
    const R = 6371; // mean radius of earth
    const lat1 = this.toRad(this.lat);
    const lng1 = this.toRad(this.lng);
    const lat2 = this.toRad(lat);
    const lng2 = this.toRad(lng);
    const dLat = lat2 - lat1;
    const dLng = lng2 - lng1;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(degrees) {
    return degrees * Math.PI / 180;
  }

  sortLocations() {
    this.locations?.forEach((location) => {
      location.distance = this.distanceToCenter(location.lat, location.lng);
    });
    this.locationList = this.locations?.sort((n1, n2) => {
      if (n1.distance > n2.distance) {
        return 1;
      }
      if (n1.distance < n2.distance) {
        return -1;
      }
      return 0;
    });
  }

  googleMapsLoaded() {
    // Google Maps requires a callback method ... even if it does not do anything
  }

}
